import { useEffect, useState } from 'react';
import {
	Collapse,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography,
	useTheme,
	Menu,
	MenuItem,
	Box,
	Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MuiDrawer, { drawerClasses } from '@mui/material/Drawer';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import BusinessIcon from '@mui/icons-material/Business';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState, useAppDispatch } from '../../redux/store';
import { fetchOffices, setOffice } from '../../redux/appSlice';
import { useSelector } from 'react-redux';
import { IRoute } from '../types';
import useDashboardRoutes from '../dashboardRoutes';
import PortalLogo from '../../components/PortalLogo';

interface SideNavProps {
	open: boolean;
	onToggleNav: () => any;
	isMobile: boolean;
}

const drawerWidth = 240;

const Drawer = styled(MuiDrawer)(({ theme }) => ({
	width: drawerWidth,
	flexShrink: 0,
	boxSizing: 'border-box',
	[`& .${drawerClasses.paper}`]: {
		width: drawerWidth,
		boxSizing: 'border-box',
		borderRight: `1px solid ${theme.palette.divider}`,
		background: theme.palette.background.default,
	},
}));

function SideNav({ open, onToggleNav, isMobile }: SideNavProps) {
	const { routes } = useDashboardRoutes();
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useAppDispatch();
	const { offices, office } = useSelector((state: RootState) => state.app);

	const currentLocation = location.pathname;
	const [expanded, setExpanded] = useState<string[]>([]);

	const toggleNav = (key: string): void => {
		setExpanded(prev => (prev.includes(key) ? prev.filter(el => el !== key) : [...prev, key]));
	};

	const onClickMenu = ({ key, path, isExternal, children }: IRoute): void => {
		if (path) {
			if (isMobile) onToggleNav();
			if (isExternal) {
				window.open(path, '_blank');
			} else {
				navigate(path);
			}
		} else if (children) {
			toggleNav(key ?? '');
		}
	};

	// Office dropdown state
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const openx = Boolean(anchorEl);
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => setAnchorEl(null);

	useEffect(() => {
		if ((offices?.length ?? 0) === 0) {
			dispatch(fetchOffices());
		}
	}, [dispatch, offices?.length]);

	return (
		<Drawer
			variant={isMobile ? 'temporary' : 'persistent'}
			open={open}
			onClose={onToggleNav}
			ModalProps={{
				keepMounted: true, // improves mobile performance
			}}
			sx={{
				display: 'block',
				zIndex: theme => (isMobile ? theme.zIndex.drawer + 2 : theme.zIndex.drawer),
				[`& .${drawerClasses.paper}`]: {
					width: drawerWidth,
					boxSizing: 'border-box',
					borderRight: theme => `1px solid ${theme.palette.divider}`,
					backgroundColor: theme => theme.palette.background.paper,
					...(isMobile && {
						boxShadow: theme.shadows[8], // ✅ visible overlay shadow on mobile
					}),
				},
			}}
		>
			<Box px={2} py={2} textAlign={'center'}>
				<PortalLogo />
			</Box>

			{/* Office Selector */}
			<Box px={2} py={1}>
				<ListItemButton
					onClick={handleClick}
					sx={{
						borderRadius: 2,
						bgcolor: theme.palette.action.hover,
						'&:hover': { bgcolor: theme.palette.action.selected },
					}}
				>
					<ListItemIcon sx={{ minWidth: 32, color: theme => theme.palette.primary.main }}>
						<BusinessIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Typography variant="body2" fontWeight={500}>
								{office?.name || 'Select Office'}
							</Typography>
						}
					/>
					{openx ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
				</ListItemButton>
			</Box>

			<Menu
				anchorEl={anchorEl}
				open={openx}
				onClose={handleClose}
				PaperProps={{
					sx: {
						maxHeight: 240,
						width: 220,
					},
				}}
			>
				{offices?.map(off => (
					<MenuItem
						key={off.id}
						onClick={() => {
							dispatch(setOffice(off));
							handleClose();
						}}
					>
						{off.name}
					</MenuItem>
				))}
			</Menu>

			{/* Routes */}
			<List component="nav" sx={{ px: 1 }}>
				{routes
					.filter(route => !route.hidden)
					.map(route => (
						<Box key={route.key}>
							<ListItemButton
								onClick={() => onClickMenu(route)}
								selected={currentLocation === route.path}
								sx={{
									my: 0.5,
									px: 2,
									borderRadius: 2,
									'&.Mui-selected': {
										bgcolor: theme.palette.action.selected,
										color: theme => theme.palette.primary.main,
										fontWeight: 'bold',
									},
								}}
							>
								<ListItemIcon sx={{ minWidth: 28, color: theme => theme.palette.primary.main }}>
									{route.icon}
								</ListItemIcon>
								<ListItemText
									primary={
										<Typography variant="body2" fontWeight={'bold'}>
											{route.label}
										</Typography>
									}
								/>
								{(route.children?.length ?? 0) > 0 &&
									(expanded.includes(route.key ?? '') ? (
										<ExpandLess fontSize="small" />
									) : (
										<ExpandMore fontSize="small" />
									))}
							</ListItemButton>

							{/* Submenu */}
							{route.children && (
								<Collapse in={expanded.includes(route.key ?? '')} timeout="auto" unmountOnExit>
									<List disablePadding>
										{route.children
											.filter(sub => !sub.hidden)
											.map(sub => (
												<ListItemButton
													key={sub.path}
													onClick={() => onClickMenu(sub)}
													selected={currentLocation === sub.path}
													sx={{
														pl: 5,
														py: 0.5,
														borderRadius: 2,
														'&.Mui-selected': {
															bgcolor: theme.palette.action.selected,
															color: theme => theme.palette.primary.main,
															fontWeight: 'bold',
														},
													}}
												>
													<ListItemIcon
														sx={{ minWidth: 24, color: theme => theme.palette.primary.main }}
													>
														{sub.icon}
													</ListItemIcon>
													<ListItemText
														primary={<Typography variant="body2">{sub.label}</Typography>}
													/>
												</ListItemButton>
											))}
									</List>
								</Collapse>
							)}
						</Box>
					))}
			</List>

			{/* Footer */}
			<Divider sx={{ mt: 'auto' }} />
			<Box
				sx={{
					py: 2,
					px: 2,
					textAlign: 'center',
					color: theme.palette.text.secondary,
				}}
			>
				<ListItemButton
					onClick={() => {
						navigate('/system-info');
						if (isMobile) onToggleNav();
					}}
					sx={{
						justifyContent: 'center',
						mt: 1,
						borderRadius: 2,
						bgcolor: theme.palette.action.hover,
						'&:hover': { bgcolor: theme.palette.action.selected },
					}}
				>
					<Typography variant="body2" sx={{ color: theme => theme.palette.primary.main }}>
						View System Info
					</Typography>
				</ListItemButton>
			</Box>
		</Drawer>
	);
}

export default SideNav;
